import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Lock, BarChart3, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import QuickRulesCard from "../components/QuickRulesCard";

const HERO_IMG = "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHN0YWRpdW0lMjBmbG9vZGxpZ2h0c3xlbnwwfHx8fDE3Nzk3NDA2NTN8MA&ixlib=rb-4.1.0&q=85";
const TROPHY_IMG = "https://static.prod-images.emergentagent.com/jobs/027426fc-29d9-47d6-bc29-4b3c61817cc2/images/3834f77b2e570f6c27739602d82508bae9d6189d36e4fd7e10400ae8dd0e3b77.png";

function StatLine({ template, value }) {
    const parts = template.split("__N__");
    return (
        <span>
            {parts[0]}
            <span className="text-[var(--text-primary)]">{value}</span>
            {parts[1] || ""}
        </span>
    );
}

export default function Landing() {
    const { user } = useAuth();
    const { t } = useI18n();
    const ctaHref = user ? "/dashboard" : "/register";
    const ctaLabel = user ? t("landing.cta.open") : t("landing.cta.create");

    // Subtitle has 5 interpolated emphasis chunks. Each chunk is wrapped in
    // [[X]]…[[/X]] sentinels so we can split + style after translation.
    const subtitle = t("landing.subtitle", {
        home: `[[H]]${t("landing.subtitle.home")}[[/H]]`,
        outcome: `[[O]]${t("landing.subtitle.outcome")}[[/O]]`,
        max: `[[M]]${t("landing.subtitle.max")}[[/M]]`,
        knock: `[[K]]${t("landing.subtitle.knock")}[[/K]]`,
        bonus: `[[B]]${t("landing.subtitle.bonus")}[[/B]]`,
    });
    const subtitleParts = subtitle
        .split(/(\[\[[HOMKB]\]\][\s\S]*?\[\[\/[HOMKB]\]\])/g)
        .map((seg, i) => {
            const m = seg.match(/^\[\[([HOMKB])\]\]([\s\S]*?)\[\[\/[HOMKB]\]\]$/);
            const key = `sub-${i}-${seg.length}`;
            if (!m) return <span key={key}>{seg}</span>;
            // Accent (yellow/green) tone vs. plain emphasis (white) — `max`,
            // `knock` and `bonus` are accent-colored to draw the eye.
            const cls = (m[1] === "M" || m[1] === "K" || m[1] === "B")
                ? "text-[var(--accent-hex)] font-semibold"
                : "text-white font-semibold";
            return <span key={key} className={cls}>{m[2]}</span>;
        });

    return (
        <div data-testid="landing-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_IMG})` }}
                    aria-hidden
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/80 to-[var(--bg-base)]" aria-hidden />
                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20 sm:pb-32">
                    <div className="max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--accent-hex)] mb-4" data-testid="hero-eyebrow">
                            {t("landing.eyebrow")}
                        </p>
                        <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter text-white leading-[0.92]">
                            {t("landing.title_1")} <span className="text-[var(--accent-hex)]">{t("landing.title_emph")}</span><br />
                            {t("landing.title_2")} <span className="italic text-white/60 font-bold">{t("landing.title_action")}</span><br />
                            {t("landing.title_3")}
                        </h1>
                        <p className="mt-6 max-w-xl text-base sm:text-lg text-white/80 leading-relaxed">
                            {subtitleParts}
                        </p>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link
                                to={ctaHref}
                                data-testid="hero-cta-primary"
                                className="group inline-flex items-center justify-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-6 py-4 rounded-md hover:bg-[var(--accent-hex-hover)] transition-colors"
                            >
                                {ctaLabel} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                                to="/login"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-bold uppercase tracking-wide px-6 py-4 rounded-md hover:border-white/70 transition-colors"
                            >
                                {t("landing.cta.have_account")}
                            </Link>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-widest font-bold text-white/70">
                            <StatLine template={t("landing.stat.teams", { n: "__N__" })} value="48" />
                            <StatLine template={t("landing.stat.matches", { n: "__N__" })} value="104" />
                        </div>
                        <p className="mt-4 text-[11px] sm:text-xs text-white/70 leading-relaxed max-w-2xl normal-case tracking-normal" data-testid="hero-credits">
                            {(() => {
                                const raw = t("landing.stat.bragging");
                                const parts = raw.split(/(\[\[L[12]\]\][\s\S]*?\[\[\/L[12]\]\])/g);
                                return parts.map((seg, i) => {
                                    const m = seg.match(/^\[\[L([12])\]\]([\s\S]*?)\[\[\/L\1\]\]$/);
                                    const key = `cr-${i}-${seg.length}`;
                                    if (!m) return <span key={key}>{seg}</span>;
                                    const href = m[1] === "1" ? "https://app.emergent.sh" : "https://www.football-data.org/client/login";
                                    return (
                                        <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--accent-hex)] underline underline-offset-2 hover:text-white font-semibold">
                                            {m[2]}
                                        </a>
                                    );
                                });
                            })()}
                        </p>
                    </div>
                </div>
            </section>

            {/* QUICK RULES */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
                <QuickRulesCard variant="landing" collapsible defaultOpen={false} />
            </section>

            {/* SCORING */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 relative" data-testid="scoring-section">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--accent-hex)] mb-3">{t("landing.scoring.eyebrow")}</p>
                        <h2 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-[var(--text-primary)] mb-6">
                            {t("landing.scoring.title_1")}<br />{t("landing.scoring.title_2")}
                        </h2>
                        <div className="space-y-4">
                            {[
                                [t("landing.scoring.home.title"), "2", t("landing.scoring.home.desc")],
                                [t("landing.scoring.away.title"), "2", t("landing.scoring.away.desc")],
                                [t("landing.scoring.outcome.title"), "3", t("landing.scoring.outcome.desc")],
                                [t("landing.scoring.knock.title"), "+2", t("landing.scoring.knock.desc")],
                                [t("landing.scoring.bonus.title"), "100", t("landing.scoring.bonus.desc")],
                            ].map(([title, pts, desc]) => (
                                <div key={title} className="flex items-start gap-4 p-4 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)]">
                                    <div className="flex-shrink-0 w-14 h-14 rounded-md bg-[var(--accent-hex)] text-[var(--accent-text)] flex items-center justify-center font-heading text-lg font-black">
                                        {pts}
                                    </div>
                                    <div>
                                        <div className="font-heading text-lg font-bold uppercase tracking-tight text-[var(--text-primary)]">{title}</div>
                                        <div className="text-sm text-[var(--text-secondary)]">{desc}</div>
                                    </div>
                                </div>
                            ))}
                            <div className="px-4 py-3 rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5 text-sm">
                                <span className="font-heading uppercase tracking-widest text-[var(--accent-hex)] text-xs font-bold">{t("landing.scoring.max")}</span>
                                <span className="ml-3 font-heading text-2xl font-extrabold text-[var(--text-primary)]">7</span>
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <img src={TROPHY_IMG} alt="Trophy" className="w-full max-w-md mx-auto" data-testid="trophy-image" />
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
                <div className="grid sm:grid-cols-3 gap-4">
                    {[
                        { key: "private", icon: <Users className="h-5 w-5" /> },
                        { key: "lock", icon: <Lock className="h-5 w-5" /> },
                        { key: "leader", icon: <BarChart3 className="h-5 w-5" /> },
                    ].map((f) => (
                        <div key={f.key} className="p-5 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] hover:border-[var(--accent-border)] hover:-translate-y-1 transition-all duration-200" data-testid={`feature-${f.key}`}>
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[var(--accent-hex)]/10 text-[var(--accent-hex)] mb-3">
                                {f.icon}
                            </div>
                            <div className="font-heading text-xl font-bold uppercase tracking-tight text-[var(--text-primary)]">{t(`landing.feature.${f.key}.title`)}</div>
                            <p className="text-sm text-[var(--text-secondary)] mt-2">{t(`landing.feature.${f.key}.desc`)}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
                <div className="relative overflow-hidden rounded-2xl border border-[var(--accent-border)] bg-gradient-to-br from-[var(--bg-card)] to-[var(--bg-base)] p-8 sm:p-12">
                    <div className="relative z-10">
                        <Trophy className="h-8 w-8 text-[var(--accent-hex)] mb-3" />
                        <h3 className="font-heading text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-[var(--text-primary)] max-w-2xl">
                            {t("landing.final.title_1")} <span className="text-[var(--accent-hex)]">{t("landing.final.title_2")}</span>
                        </h3>
                        <p className="mt-3 text-[var(--text-secondary)] max-w-lg">
                            {t("landing.final.desc")}
                        </p>
                        <Link
                            to={ctaHref}
                            data-testid="final-cta"
                            className="mt-6 inline-flex items-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-6 py-4 rounded-md hover:bg-[var(--accent-hex-hover)] transition-colors"
                        >
                            {ctaLabel} <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
